import axios from "axios";
import { useState, useEffect } from "react";
import Seo from '../../seo/Seo';
import Cookies from "js-cookie";
import { BsEnvelope, BsEye, BsEyeSlash, BsKey } from "react-icons/bs";
import LoginIllustration from "./LoginIllustration";
import TenantPicker from "./TenantPicker";
import "./LoginPage.css";
import {
  API_BASE_URL,
  API_AUTH_ENDPOINT,
  API_VERIFY_MFA_ENDPOINT,
  DASHBOARD_APP_URI,
  DOMAIN,
  TOKEN_EMAIL_CLAIMS,
  TOKEN_USER_GIVEN_NAME,
  BI_APP_URI,
} from "../../constants/constants";
import { useToken } from "../useToken";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { jwtDecode } from "jwt-decode";

export const LoginPage = ({ authSession, onLogin }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [token, setToken] = useToken();
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState(location.state?.message || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [signedInEmail, setSignedInEmail] = useState("");
  const [signedInName, setSignedInName] = useState("");
  const [requireMfa, setRequireMfa] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [qrCodeImage, setQrCodeImage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [tenants, setTenants] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const loginEndpoint = API_BASE_URL + API_AUTH_ENDPOINT;
  const verifyMfaEndpoint =
    API_BASE_URL + API_AUTH_ENDPOINT + API_VERIFY_MFA_ENDPOINT;

  const getPayloadFromToken = (token) => {
    const encodedPayload = token.split(".")[1];
    return JSON.parse(atob(encodedPayload));
  };

  useEffect(() => {
    const storedToken =
      Cookies.get("authToken") || localStorage.getItem("authToken");
    if (storedToken) {
      if (getPayloadFromToken(storedToken).exp > Date.now() / 1000) {
        const decodedToken = getPayloadFromToken(storedToken);
        setSignedInEmail(decodedToken[TOKEN_EMAIL_CLAIMS]);
        setSignedInName(decodedToken[TOKEN_USER_GIVEN_NAME]);
      }
    }
  }, []);

  const handleLogin = async (accountId = null) => {
    setIsLoading(true);
    try {
      const payload = {
        emailAddress: email,
        password: password,
      };
      if (accountId) {
        payload.accountId = accountId;
      }
      const response = await axios.post(loginEndpoint, payload);
      const { token, requireMfa, qrCodeImageAsBase64, requireTenantSelection, tenants: tenantList } = response.data;

      if (requireTenantSelection && tenantList) {
        setTenants(tenantList);
        setErrorMessage("");
      } else if (requireMfa) {
        setTenants([]);
        setSelectedAccountId(response.data.accountId || accountId);
        setRequireMfa(true);
        setErrorMessage("");
      } else if (qrCodeImageAsBase64) {
        setTenants([]);
        setSelectedAccountId(response.data.accountId || accountId);
        setQrCodeImage(qrCodeImageAsBase64);
        setErrorMessage("");
      } else {
        setToken(token);
        if (rememberMe) {
          localStorage.setItem("authToken", token);
        }
        Cookies.set("authToken", token, {
          domain: DOMAIN,
          secure: true,
          sameSite: "None",
          path: "/",
        });
        setErrorMessage("");
        onLogin(true);
        navigateUserBasedOnLicense(token);
      }
    } catch (error) {
      setTenants([]);
      setErrorMessage(
        error.response?.status === 401
          ? "Invalid email or password"
          : "Something went wrong. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async (code) => {
    const mfaCode = code || verificationCode;
    if (mfaCode.length !== 6) {
      setErrorMessage("Enter a 6-digit code.");
      return;
    }
    try {
      setIsLoading(true);
      const response = await axios.post(verifyMfaEndpoint, {
        emailAddress: email,
        totpCode: mfaCode,
        accountId: selectedAccountId,
      });
      if (response.status === 200) {
        const { token } = response.data;
        setToken(token);
        Cookies.set("authToken", token, {
          domain: DOMAIN,
          secure: true,
          sameSite: "None",
          path: "/",
        });
        navigateUserBasedOnLicense(token);
      } else {
        setErrorMessage("Invalid code. Try again.");
      }
    } catch (error) {
      setErrorMessage("Verification failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleUseAnotherAccount = () => {
    setToken(null);
    Cookies.remove("authToken", { domain: DOMAIN, path: "/" });
    setSignedInEmail("");
    setEmail("");
    setPassword("");
  };

  const redirectToApp = () => {
    const storedToken =
      localStorage.getItem("authToken") || Cookies.get("authToken");
    if (storedToken) {
      navigateUserBasedOnLicense(storedToken);
    } else {
      handleUseAnotherAccount();
    }
  };

  const navigateUserBasedOnLicense = (tc = null) => {
    const activeToken = tc || token;
    const decodedToken = jwtDecode(activeToken);
    if (decodedToken?.RayaLicense === "BI") {
      window.location.href = `${BI_APP_URI}dashboard?token=${activeToken}`;
    } else {
      // BS or BIAndBS — go directly to Business Suite
      window.location.href = `${DASHBOARD_APP_URI}dashboard?token=${activeToken}`;
    }
  };

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      if (!email) setErrorMessage("Email is required");
      else if (!validateEmail(email)) setErrorMessage("Invalid email");
      else if (!password) setErrorMessage("Password is required");
      else handleLogin();
    }
  };

  const handleSelectTenant = (accountId) => {
    setSelectedAccountId(accountId);
    // Keep the picker mounted (with its spinner) until the tenant login resolves.
    handleLogin(accountId);
  };

  // Tenant picker view
  if (tenants.length > 0) {
    return (
      <div className="login-page">
        <Seo path="/login" />
        <div className="login-card login-card--wide">
          <div className="login-card-inner">
            <TenantPicker
              tenants={tenants}
              email={email}
              isLoading={isLoading}
              onSelect={handleSelectTenant}
              onBack={() => { setTenants([]); setPassword(""); }}
            />
          </div>
        </div>
      </div>
    );
  }

  // MFA / QR code view
  if (requireMfa || qrCodeImage) {
    return (
      <div className="login-page">
        <Seo path="/login" />
        <div className="login-card">
          <div className="login-card-inner">
            <div className="login-icon">🔐</div>
            <h1>Two-Factor Authentication</h1>
            {qrCodeImage && (
              <div className="qr-section">
                <p className="mfa-hint">Scan with your authenticator app</p>
                <img
                  src={`data:image/png;base64,${qrCodeImage}`}
                  alt="QR Code"
                  className="qr-image"
                />
              </div>
            )}
            <p className="mfa-hint">Enter the 6-digit code</p>
            <input
              type="tel"
              className={`input-field mfa-input ${errorMessage ? "input-error" : ""}`}
              value={verificationCode}
              maxLength="6"
              placeholder="000000"
              onInput={(e) => {
                const value = e.target.value.replace(/\D/g, "");
                setVerificationCode(value);
                if (value.length === 6) handleVerifyCode(value);
              }}
            />
            {errorMessage && <p className="error-msg">{errorMessage}</p>}
            {isLoading && <div className="spinner" />}
            <a href="/forgot-password" className="link-small">Recover account</a>
          </div>
        </div>
      </div>
    );
  }

  // Already signed-in view
  if (signedInEmail) {
    return (
      <div className="login-page">
        <Seo path="/login" />
        <div className="login-card">
          <div className="login-card-inner">
            <h1>Welcome back</h1>
            <div className="signed-in-tile" onClick={redirectToApp}>
              <div className="avatar">{signedInName?.charAt(0)?.toUpperCase()}</div>
              <div>
                <p className="signed-in-name">{signedInName}</p>
                <p className="signed-in-email">{signedInEmail}</p>
              </div>
              <span className="arrow-right">→</span>
            </div>
            <button className="link-btn" onClick={handleUseAnotherAccount}>
              Use a different account
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Default login form
  return (
    <div className="login-page">
      <Seo path="/login" />
      <div className="login-card">
        <div className="login-card-inner">
          <LoginIllustration />
          <h1>Sign in to Raya</h1>
          <p className="login-sub">Enter your credentials to continue</p>

          <div className="input-group">
            <span className="input-icon"><BsEnvelope /></span>
            <input
              type="email"
              className="input-field"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyPress={handleKeyPress}
            />
          </div>

          <div className="input-group">
            <span className="input-icon"><BsKey /></span>
            <input
              type={showPassword ? "text" : "password"}
              className="input-field"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyPress={handleKeyPress}
            />
            <span className="input-icon-right" onClick={() => setShowPassword(!showPassword)}>
              {showPassword ? <BsEyeSlash /> : <BsEye />}
            </span>
          </div>

          <div className="login-options">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              />
              Remember me
            </label>
            <Link to="/forgot-password" className="link-small">Forgot password?</Link>
          </div>

          {successMessage && <p style={{ color: "#059669", fontSize: "13px", textAlign: "center", margin: "0 0 8px 0" }}>{successMessage}</p>}
          {errorMessage && <p className="error-msg">{errorMessage}</p>}

          <button
            className="btn-submit"
            onClick={() => handleLogin()}
            disabled={!email || !password || isLoading}
          >
            {isLoading ? "Signing in..." : "Sign In"}
          </button>

          <p className="login-footer-text">
            Don't have an account? <Link to="/register" className="link-accent">Sign up free</Link>
          </p>
        </div>
      </div>
    </div>
  );
};
